// PersonalDetails.js
import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { FaArrowLeft } from 'react-icons/fa';

const PersonalDetails = ({ context }) => {
  const {
    personalDetails,
    setPersonalDetails,
    loading,
    setUser,
    handlePersonalDetailsNext
  } = context;

  const [loginStep, setLoginStep] = useState('options');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [formErrors, setFormErrors] = useState({
    name: false,
    email: false,
    phone: false
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    const storedUserRaw = localStorage.getItem('user');
    const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
    if (storedUser) {
      updateUserDetails(storedUser);
      setLoginStep('form');
    } else if (personalDetails.email) {
      setEmail(personalDetails.email);
      setLoginStep('form');
    }
  }, [personalDetails.email]);

  const updateUserDetails = (user) => {
    const updates = {};
    if (!personalDetails.name && user.name) updates.name = user.name;
    if (!personalDetails.email && user.email) updates.email = user.email;
    if (!personalDetails.phone && user.phone) updates.phone = user.phone;

    if (Object.keys(updates).length > 0) {
      setPersonalDetails(prev => ({ ...prev, ...updates }));
    }

    if (user.name || user.phone) {
      setIsExistingUser(true);
    }
    setUser(user);
  };

  const validateForm = () => {
    const errors = {
      name: !personalDetails.name,
      email: !(personalDetails.email || email),
      phone: !personalDetails.phone
    };
    setFormErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const isFormValid = Boolean(
    personalDetails.name &&
    (personalDetails.email || email) &&
    personalDetails.phone
  );

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/send-otp`, { email });
      if (response.data.success) {
        setLoginStep('otp');
      } else {
        setError(response.data.error || 'Failed to send OTP');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!/^\d{6}$/.test(otp)) {
      setError('Please enter a valid 6-digit OTP');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/verify-otp`, { email, otp });
      if (response.data.success) {
        const { user } = response.data;
        localStorage.setItem('user', JSON.stringify(user));
        updateUserDetails(user);
        setLoginStep('form');
      } else {
        setError(response.data.error || 'Invalid OTP');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    setIsLoading(true);
    setError('');

    try {
      const userInfo = await axios.get(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
      );

      const response = await axios.post(`${API_BASE_URL}/google-login`, {
        email: userInfo.data.email,
        name: userInfo.data.name,
        authMethod: 'google'
      });

      if (response.data.success) {
        const user = response.data.user;
        localStorage.setItem('user', JSON.stringify(user));
        updateUserDetails(user);
        setLoginStep('form');
      } else {
        setError(response.data.error || 'Google login failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Google login failed. Please try another method.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!isGuest) {
      try {
        const response = await axios.post(`${API_BASE_URL}/update-user`, {
          email: personalDetails.email || email,
          name: personalDetails.name,
          phone: personalDetails.phone
        });
        if (response.data.user) {
          setUser(response.data.user);
        }
      } catch {}
    }

    handlePersonalDetailsNext();
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => setError('Google login failed. Please try again.')
  });

  const handleContinueAsGuest = () => {
    setIsGuest(true);
    setLoginStep('form');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPersonalDetails(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: false }));
  };

  const renderLoginOptions = () => (
    <div className="addon-sheet login-options">
      <h3>Login or continue as guest</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSendOtp}>
        <div className="form-group">
          <input
            type="email"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={`form-control email-otpinput ${formErrors.email ? 'is-invalid' : ''}`}
          />
          {formErrors.email && <div className="invalid-feedback">Please provide a valid email</div>}
        </div>
        <button
          type="submit"
          className="w-100 btn btn-primary btn-block"
          disabled={isLoading}
        >
          {isLoading ? 'Sending...' : 'Send OTP'}
        </button>
      </form>

      <div className="divider or-divider">
        <span>OR</span>
      </div>

      <button
        onClick={() => googleLogin()}
        className="btn btn-danger btn-block"
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : 'Login with Google'}
      </button>

      <button
        onClick={handleContinueAsGuest}
        className="btn btn-outline-secondary btn-block"
      >
        Continue as Guest
      </button>
    </div>
  );

  const renderOtpForm = () => (
    <div className="addon-sheet otp-form">
      <h3>Enter OTP sent to {email}</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleVerifyOtp}>
        <div className="form-group">
          <input
            type="text"
            placeholder="6-digit OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            className={`form-control email-otpinput ${error ? 'is-invalid' : ''}`}
            autoFocus
          />
          {error && <div className="invalid-feedback">{error}</div>}
        </div>
        <button
          type="submit"
          className="w-100 btn btn-primary btn-block"
          disabled={isLoading}
        >
          {isLoading ? 'Verifying...' : 'Verify OTP'}
        </button>
      </form>

      <button
        onClick={() => {
          setLoginStep('options');
          setOtp('');
        }}
        className="btn btn-link"
      >
        Back to login options
      </button>
    </div>
  );

  const renderDetailsForm = () => (
    <form onSubmit={handleNext} className="addon-sheet details-form">
      {isGuest && (
        <div className="alert alert-info">
          You're continuing as guest. Some features may be limited.
        </div>
      )}

      {isExistingUser && (
        <div className="alert alert-success">
          Welcome back! Your saved information has been loaded.
        </div>
      )}

      <div className="form-group">
        <label>Full Name *</label>
        <input
          type="text"
          name="name"
          value={personalDetails.name}
          onChange={handleInputChange}
          className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
        />
        {formErrors.name && <div className="invalid-feedback">Please provide your full name</div>}
      </div>

      <div className="form-group">
        <label>Email *</label>
        <input
          type="email"
          name="email"
          value={personalDetails.email || email}
          onChange={handleInputChange}
          className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
          disabled={!isGuest && (personalDetails.email || email)}
        />
        {formErrors.email && <div className="invalid-feedback">Please provide a valid email</div>}
      </div>

      <div className="form-group">
        <label>Phone Number *</label>
        <input
          type="tel"
          name="phone"
          value={personalDetails.phone}
          onChange={handleInputChange}
          className={`form-control ${formErrors.phone ? 'is-invalid' : ''}`}
        />
        {formErrors.phone && <div className="invalid-feedback">Please provide your phone number</div>}
      </div>

      <div className="form-group">
        <label>Flight Number (optional)</label>
        <input
          type="text"
          name="flightNumber"
          value={personalDetails.flightNumber}
          onChange={handleInputChange}
          className="form-control"
        />
      </div>

      <div className="form-group">
        <label>Special Notes (optional)</label>
        <textarea
          name="extraNotes"
          value={personalDetails.extraNotes}
          onChange={handleInputChange}
          className="form-control"
          rows="3"
        />
      </div>
    </form>
  );

  return (
    <div className="personal-details-container">
      <div className="">
        {loginStep === 'options' && renderLoginOptions()}
        {loginStep === 'otp' && renderOtpForm()}
        {loginStep === 'form' && renderDetailsForm()}
      </div>

      <div className="footer-bar">
        <button
          className="back-btn"
          onClick={context.handleBack}
        >
          <FaArrowLeft />
        </button>
        {loginStep === 'form' && (
          <button
            className={`btn-proceed ${loading ? 'disabled' : ''}`}
            onClick={handleNext}
            disabled={loading || !isFormValid}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Loading...
              </>
            ) : (
              'CONTINUE TO PAYMENT'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default PersonalDetails;
